import React from "react";
import { Client, User, YearWithProjects } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Pin, Plus, X, ChevronDown, Filter, GanttChart, RotateCcw } from "lucide-react";
import { IconTooltip } from "@/components/ui/icon-tooltip";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { getUserColor } from '@/lib/user-color';

interface ProjectFilterBarProps {
  searchFilter: string;
  onSearchChange: (value: string) => void;

  isTimelineMode?: boolean;
  onTimelineModeToggle?: () => void;
  /** Called when the user clicks the "reset timeline view" button. Only
   *  rendered when timeline mode is on. */
  onTimelineResetView?: () => void;

  pinnedEnabled: boolean;
  onPinnedToggle: (enabled: boolean) => void;

  hideFinals: boolean;
  onHideFinalsToggle: (enabled: boolean) => void;
  hideCanceled: boolean;
  onHideCanceledToggle: (enabled: boolean) => void;

  hideWithEfforts: boolean;
  onHideWithEffortsToggle: (enabled: boolean) => void;

  clientFilter: string;
  leadArtistFilter: string;
  onClientFilterChange: (clientId: string) => void;
  onLeadArtistFilterChange: (artistCode: string) => void;
  
  clients: Client[];
  artists: User[];
  
  pinnedProjects: Array<{projectId: string, title: string}>;
  onPinnedProjectsChange: (projects: Array<{projectId: string, title: string}>) => void;
  
  pinnedOpen: boolean;
  onPinnedOpenChange: (open: boolean) => void;
  
  showAddToPinned: boolean;
  onShowAddToPinnedChange: (show: boolean) => void;
  
  filteredYears: YearWithProjects[];
  
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

export function ProjectFilterBar({
  searchFilter,
  onSearchChange,
  isTimelineMode = false,
  onTimelineModeToggle,
  onTimelineResetView,
  pinnedEnabled,
  onPinnedToggle,
  hideFinals,
  onHideFinalsToggle,
  hideCanceled,
  onHideCanceledToggle,
  hideWithEfforts,
  onHideWithEffortsToggle,
  clientFilter,
  leadArtistFilter,
  onClientFilterChange,
  onLeadArtistFilterChange,
  clients,
  artists,
  pinnedProjects,
  onPinnedProjectsChange,
  pinnedOpen,
  onPinnedOpenChange,
  showAddToPinned,
  onShowAddToPinnedChange,
  filteredYears,
  hasActiveFilters,
  onClearFilters
}: ProjectFilterBarProps) {
  const [filterDropdownOpen, setFilterDropdownOpen] = React.useState(false);

  const selectedClient = clients.find(c => c.id === clientFilter);
  const selectedArtist = artists.find(a => a.shortCode === leadArtistFilter);

  const availableClients = clients.filter(c => c.id !== clientFilter);
  const availableArtists = artists.filter(a => a.shortCode !== leadArtistFilter);

  const handleSelectClient = (clientId: string) => {
    onClientFilterChange(clientId);
  };

  const handleSelectArtist = (artistCode: string) => {
    onLeadArtistFilterChange(artistCode);
  };

  const handleRemoveClientFilter = () => {
    onClientFilterChange("");
  };

  const handleRemoveArtistFilter = () => {
    onLeadArtistFilterChange("");
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Filters Dropdown */}
      <Popover open={filterDropdownOpen} onOpenChange={setFilterDropdownOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="bg-gray-800 hover:bg-gray-700 text-gray-300 h-8 px-3 border-gray-600"
            data-testid="filters-dropdown-button"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {/* Canceled projects are hidden by default; only SHOWING them counts as an active filter. */}
            {(searchFilter || clientFilter || leadArtistFilter || hideFinals || !hideCanceled || hideWithEfforts) && (
              <Badge className="ml-2 bg-blue-600 text-white h-5 px-1.5 text-xs">
                {[searchFilter, clientFilter, leadArtistFilter, hideFinals, !hideCanceled, hideWithEfforts].filter(Boolean).length}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-80 p-0 bg-gray-900 border-gray-700" 
          align="start"
        >
          <div className="p-3 space-y-3">
            {/* Search Projects */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Search Projects</label>
              <Input
                data-testid="filter-search-input"
                placeholder="Type to search..."
                value={searchFilter}
                onChange={(e) => onSearchChange(e.target.value)}
                className="h-8 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 text-sm"
              />
            </div>

            {/* Client Filter */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Client</label>
              {selectedClient ? (
                <Badge 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 flex items-center gap-2 w-full justify-between"
                >
                  <span className="text-xs flex items-center gap-2">
                    <span className="font-mono text-xs bg-gray-700 px-1 rounded">
                      {selectedClient.id}
                    </span>
                    {selectedClient.companyName}
                  </span>
                  <button
                    onClick={handleRemoveClientFilter}
                    className="hover:bg-blue-800 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ) : (
                <Command className="bg-gray-800 border border-gray-700 rounded-md">
                  <CommandInput
                    placeholder="Search clients..."
                    data-testid="filter-client-search"
                    className="h-8 text-white placeholder:text-gray-500 text-sm border-b border-gray-700"
                  />
                  <CommandList>
                    <CommandEmpty className="text-gray-400 text-xs py-4 text-center">
                      No clients found
                    </CommandEmpty>
                    <CommandGroup className="max-h-48 overflow-y-auto">
                      {availableClients.map((client) => (
                        <CommandItem
                          key={client.id}
                          value={`${client.id} ${client.companyName}`}
                          onSelect={() => handleSelectClient(client.id)}
                          className="text-white hover:bg-gray-700 cursor-pointer text-sm"
                        >
                          <span className="font-mono text-xs bg-gray-700 px-1 rounded mr-2">
                            {client.id}
                          </span>
                          {client.companyName}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              )}
            </div>

            {/* Artist Filter */}
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Lead Artist</label>
              {selectedArtist ? (
                <Badge 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 flex items-center gap-2 w-full justify-between"
                >
                  <span className="text-xs">{selectedArtist.shortCode} - {selectedArtist.firstName} {selectedArtist.lastName}</span>
                  <button
                    onClick={handleRemoveArtistFilter}
                    className="hover:bg-blue-800 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ) : (
                <Command className="bg-gray-800 border border-gray-700 rounded-md">
                  <CommandList>
                    <CommandEmpty className="text-gray-400 text-xs py-4 text-center">
                      No artists found
                    </CommandEmpty>
                    <CommandGroup className="max-h-48 overflow-y-auto">
                      {availableArtists.map((artist) => (
                        <CommandItem
                          key={artist.shortCode}
                          value={`artist-${artist.shortCode}-${artist.firstName}-${artist.lastName}`}
                          onSelect={() => handleSelectArtist(artist.shortCode || '')}
                          className="text-white hover:bg-gray-700 cursor-pointer text-sm"
                        >
                          <span 
                            className="inline-block min-w-[32px] h-6 text-xs text-white rounded mr-2 text-center leading-6 font-mono"
                            style={{ backgroundColor: getUserColor(artist.colorCode) }}
                          >
                            {artist.shortCode}
                          </span>
                          {artist.firstName} {artist.lastName}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              )}
            </div>

            {/* Hide Finals toggle */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-700">
              <label
                htmlFor="hide-finals-toggle"
                className="text-sm text-white cursor-pointer flex items-center gap-2"
              >
                Hide Finals
                <kbd className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-gray-600 bg-gray-800 px-1.5 font-mono text-[10px] text-gray-300">
                  f
                </kbd>
              </label>
              <Switch
                id="hide-finals-toggle"
                checked={hideFinals}
                onCheckedChange={onHideFinalsToggle}
                data-testid="hide-finals-toggle"
              />
            </div>

            {/* Hide with Efforts toggle */}
            <div className="flex items-center justify-between">
              <label
                htmlFor="hide-with-efforts-toggle"
                className="text-sm text-white cursor-pointer flex items-center gap-2"
              >
                Hide with Efforts
                <kbd className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-gray-600 bg-gray-800 px-1.5 font-mono text-[10px] text-gray-300">
                  e
                </kbd>
              </label>
              <Switch
                id="hide-with-efforts-toggle"
                checked={hideWithEfforts}
                onCheckedChange={onHideWithEffortsToggle}
                data-testid="hide-with-efforts-toggle"
              />
            </div>

            <div className="flex items-center justify-between">
              <label htmlFor="show-canceled-toggle" className="text-sm text-gray-300 cursor-pointer">
                Show Canceled
              </label>
              <Switch
                id="show-canceled-toggle"
                checked={!hideCanceled}
                onCheckedChange={(show) => onHideCanceledToggle(!show)}
                data-testid="show-canceled-toggle"
              />
            </div>

            {/* Clear All */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onClearFilters();
                  setFilterDropdownOpen(false);
                }}
                className="w-full text-red-400 hover:text-red-300 hover:bg-gray-800"
              >
                Clear All Filters
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Pinned Projects Dropdown */}
      <Popover open={pinnedOpen} onOpenChange={onPinnedOpenChange}>
        <TooltipProvider delayDuration={250}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={pinnedOpen}
                  data-testid="pinned-projects-dropdown"
                  className={cn(
                    "w-40 justify-between h-8 border-gray-600 text-white text-sm",
                    !pinnedEnabled && "bg-gray-800 hover:bg-gray-700"
                  )}
                  style={pinnedEnabled ? { backgroundColor: '#6889c4' } : undefined}
                >
                  <Pin className="mr-2 h-4 w-4" />
                  Pinned
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-gray-800 text-gray-200 text-xs border-gray-600">
              Show only pinned projects
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <PopoverContent className="w-80 p-0 bg-gray-800 border-gray-600">
          {!showAddToPinned ? (
            <div className="p-3">
              <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-600">
                <Checkbox
                  id="enable-pinned"
                  checked={pinnedEnabled}
                  onCheckedChange={(checked) => onPinnedToggle(!!checked)}
                  data-testid="pinned-projects-checkbox"
                />
                <label
                  htmlFor="enable-pinned"
                  className="text-sm font-medium text-white cursor-pointer"
                >
                  Show only pinned projects
                </label>
              </div>
              
              {pinnedProjects.length > 0 && (
                <div className="space-y-1 mb-3 max-h-48 overflow-y-auto">
                  {pinnedProjects.map((project) => (
                    <div
                      key={project.projectId}
                      data-testid={`pinned-project-item-${project.projectId}`}
                      className="flex items-center justify-between p-2 rounded bg-gray-700 hover:bg-gray-600 transition-colors"
                    >
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-xs font-mono text-gray-400">{project.projectId}</span>
                        <span className="text-sm text-white truncate">{project.title}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          onPinnedProjectsChange(
                            pinnedProjects.filter(p => p.projectId !== project.projectId)
                          );
                        }}
                        className="ml-2 h-6 w-6 p-0 text-gray-400 hover:text-red-400 hover:bg-gray-800"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => onShowAddToPinnedChange(true)}
                data-testid="add-pinned-project-button"
                className="w-full bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Project
              </Button>
            </div>
          ) : (
            <Command className="bg-gray-800">
              <CommandInput 
                placeholder="Search projects..." 
                className="text-white border-gray-600"
              />
              <CommandEmpty className="text-gray-400 py-6 text-center text-sm">
                No project found.
              </CommandEmpty>
              <CommandList>
                <CommandGroup className="max-h-64 overflow-y-auto">
                  {filteredYears.flatMap(year => 
                    year.projects?.map((project) => {
                      const isAlreadyPinned = pinnedProjects.some(p => p.projectId === project.id);
                      if (isAlreadyPinned) return null;
                      
                      return (
                        <CommandItem
                          key={project.id}
                          value={`${project.id} ${project.projectName}`}
                          onSelect={() => {
                            onPinnedProjectsChange([
                              ...pinnedProjects,
                              { projectId: project.id, title: project.projectName }
                            ]);
                            onShowAddToPinnedChange(false);
                          }}
                          className="text-white hover:bg-gray-700"
                        >
                          <div className="flex flex-col">
                            <span className="text-xs font-mono text-gray-400">{project.id}</span>
                            <span className="text-sm">{project.projectName}</span>
                          </div>
                        </CommandItem>
                      );
                    })
                  ).filter(Boolean)}
                </CommandGroup>
              </CommandList>
              <div className="border-t border-gray-600 p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onShowAddToPinnedChange(false)}
                  className="w-full text-gray-400 hover:text-white hover:bg-gray-700"
                >
                  Cancel
                </Button>
              </div>
            </Command>
          )}
        </PopoverContent>
      </Popover>

      {/* Timeline Mode Toggle - right of Pinned, hidden below lg breakpoint */}
      {onTimelineModeToggle && (
        <IconTooltip label="Toggle timeline view (T)">
          <Button
            variant="outline"
            size="sm"
            onClick={onTimelineModeToggle}
            aria-pressed={isTimelineMode}
            aria-label={isTimelineMode ? "Switch to table view (T)" : "Switch to timeline view (T)"}
            title={isTimelineMode ? "Switch to table view (T)" : "Switch to timeline view (T)"}
            data-testid="timeline-mode-toggle"
            className={cn(
              "hidden lg:inline-flex h-8 px-2 border-gray-600 text-white",
              isTimelineMode
                ? "bg-blue-600 hover:bg-blue-500 border-blue-500"
                : "bg-gray-800 hover:bg-gray-700",
            )}
          >
            <GanttChart className="h-4 w-4" />
          </Button>
        </IconTooltip>
      )}

      {/* Reset timeline view — clears persisted zoom/pan and re-centers on
          the default window. Only visible while timeline mode is on. */}
      {isTimelineMode && onTimelineResetView && (
        <IconTooltip label="Re-center on today and clear zoom">
          <Button
            variant="outline"
            size="sm"
            onClick={onTimelineResetView}
            aria-label="Re-center on today and clear zoom"
            title="Re-center on today and clear zoom"
            data-testid="timeline-reset-view"
            className="hidden lg:inline-flex h-8 px-2 border-gray-600 text-white bg-gray-800 hover:bg-gray-700"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </IconTooltip>
      )}
    </div>
  );
}
